import type {
  GetStaffRegisterResponse,
  GetTimeReportsV2SummaryResponse,
  TimeReportsV2Filter,
  TimeReportsV2Group,
  TimeReportsV2SortBy,
} from "services/api/company/timeReports.service";

export type TimeReportsV2GroupByOption = "person" | "team";

export type TimeReportsV2DisplayOption = "simple" | "detailed";

export interface TimeReportsV2Preferences {
  readonly displayOption: TimeReportsV2DisplayOption;
  readonly groupByOption: TimeReportsV2GroupByOption;
}

export interface TimeReportsV2ListQuery {
  readonly filters?: TimeReportsV2Filter[];
  readonly fromDate: string;
  readonly pageSize: number;
  readonly sortBy: TimeReportsV2SortBy;
  readonly toDate: string;
  readonly workGroupIds: number[];
  readonly workerIds: number[];
}

export interface TimeReportsV2SummaryQuery {
  readonly fromDate: string;
  readonly toDate: string;
  readonly workGroupIds: number[];
  readonly workerIds: number[];
}

export interface TimeReportsV2StaffRegisterQuery {
  readonly fromDate: string;
  readonly pageSize: number;
  readonly toDate: string;
}

export interface TimeReportsV2ListData {
  readonly items: TimeReportsV2Group[];
  readonly total: number;
}

interface TimeReportsV2LoadingPayload {
  readonly requestId: number;
  readonly value: boolean;
}

interface TimeReportsV2RequestError {
  readonly err: Error;
  readonly message: string;
}

export type TimeReportsV2Actions =
  | {
      readonly type: "SET_LIST_REFRESHING";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_LIST_LOADING";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_LIST_LOADING_MORE";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_LIST_REQUEST_ERROR";
      readonly payload: TimeReportsV2State["listError"];
      readonly requestId: number;
    }
  | {
      readonly type: "SET_LIST";
      readonly payload: {
        readonly append: boolean;
        readonly data: TimeReportsV2ListData | null;
        readonly page: number;
        readonly pageSize: number;
        readonly requestId: number;
      };
    }
  | {
      readonly type: "SET_ACTIVE_LIST_REQUEST_ID";
      readonly payload: number | null;
    }
  | { readonly type: "RESET_LIST" }
  | {
      readonly type: "SET_LIST_QUERY";
      readonly payload: TimeReportsV2ListQuery | null;
    }
  | {
      readonly type: "SET_SUMMARY_QUERY";
      readonly payload: TimeReportsV2SummaryQuery | null;
    }
  | {
      readonly type: "SET_SUMMARY_LOADING";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_ACTIVE_SUMMARY_REQUEST_ID";
      readonly payload: number | null;
    }
  | {
      readonly type: "SET_SUMMARY_REQUEST_ERROR";
      readonly payload: TimeReportsV2State["summaryError"];
    }
  | {
      readonly type: "SET_SUMMARY";
      readonly payload: {
        readonly data: GetTimeReportsV2SummaryResponse | null;
        readonly requestId: number;
      };
    }
  | {
      readonly type: "SET_COUNT_LOADING";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_ACTIVE_COUNT_REQUEST_ID";
      readonly payload: number | null;
    }
  | {
      readonly type: "SET_COUNT_REQUEST_ERROR";
      readonly payload: TimeReportsV2State["countError"];
    }
  | {
      readonly type: "SET_COUNT";
      readonly payload: { readonly requestId: number; readonly value: number };
    }
  | {
      readonly type: "SET_PREFERENCES_REQUEST_ERROR";
      readonly payload: TimeReportsV2State["preferencesError"];
    }
  | {
      readonly type: "SET_STAFF_REGISTER_LOADING";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_STAFF_REGISTER_LOADING_MORE";
      readonly payload: TimeReportsV2LoadingPayload;
    }
  | {
      readonly type: "SET_ACTIVE_STAFF_REGISTER_REQUEST_ID";
      readonly payload: number | null;
    }
  | {
      readonly type: "SET_STAFF_REGISTER_REQUEST_ERROR";
      readonly payload: {
        readonly requestId: number;
        readonly value: TimeReportsV2State["staffRegisterError"];
      };
    }
  | {
      readonly type: "SET_STAFF_REGISTER";
      readonly payload: {
        readonly append: boolean;
        readonly data: GetStaffRegisterResponse | null;
        readonly page: number;
        readonly pageSize: number;
        readonly requestId: number;
      };
    }
  | {
      readonly type: "SET_STAFF_REGISTER_QUERY";
      readonly payload: TimeReportsV2StaffRegisterQuery | null;
    }
  | {
      readonly type: "SET_PREFERENCES";
      readonly payload: TimeReportsV2Preferences;
    }
  | {
      readonly type: "SET_GROUP_BY_OPTION";
      readonly payload: TimeReportsV2GroupByOption;
    }
  | {
      readonly type: "SET_DISPLAY_OPTION";
      readonly payload: TimeReportsV2DisplayOption;
    };

export interface TimeReportsV2State {
  readonly activeCountRequestId: number | null;
  readonly activeListRequestId: number | null;
  readonly activeStaffRegisterRequestId: number | null;
  readonly activeSummaryRequestId: number | null;
  readonly count: number;
  readonly countError: TimeReportsV2RequestError | null;
  readonly isCountLoading: boolean;
  readonly isListLoading: boolean;
  readonly isListLoadingMore: boolean;
  readonly isListRefreshing: boolean;
  readonly isStaffRegisterLoading: boolean;
  readonly isStaffRegisterLoadingMore: boolean;
  readonly isSummaryLoading: boolean;
  readonly list: TimeReportsV2ListData | null;
  readonly listError: TimeReportsV2RequestError | null;
  readonly listQuery: TimeReportsV2ListQuery | null;
  readonly page: number;
  readonly pageSize: number;
  readonly preferences: TimeReportsV2Preferences;
  readonly preferencesError: TimeReportsV2RequestError | null;
  readonly staffRegister: GetStaffRegisterResponse | null;
  readonly staffRegisterError: TimeReportsV2RequestError | null;
  readonly staffRegisterHasMore: boolean;
  readonly staffRegisterPage: number;
  readonly staffRegisterQuery: TimeReportsV2StaffRegisterQuery | null;
  readonly summary: GetTimeReportsV2SummaryResponse | null;
  readonly summaryError: TimeReportsV2RequestError | null;
  readonly summaryQuery: TimeReportsV2SummaryQuery | null;
}

export const timeReportsV2InitState: TimeReportsV2State = {
  isListLoading: false,
  isListLoadingMore: false,
  isListRefreshing: false,
  list: null,
  page: 0,
  pageSize: 0,
  listError: null,
  activeListRequestId: null,
  listQuery: null,
  summary: null,
  isSummaryLoading: false,
  summaryError: null,
  activeSummaryRequestId: null,
  summaryQuery: null,
  count: 0,
  isCountLoading: false,
  countError: null,
  activeCountRequestId: null,
  preferencesError: null,
  staffRegister: null,
  isStaffRegisterLoading: false,
  isStaffRegisterLoadingMore: false,
  staffRegisterPage: 0,
  staffRegisterHasMore: false,
  staffRegisterError: null,
  activeStaffRegisterRequestId: null,
  staffRegisterQuery: null,
  preferences: {
    groupByOption: "person",
    displayOption: "simple",
  },
};

export const timeReportsV2StateReducer = (
  state: TimeReportsV2State,
  action: TimeReportsV2Actions,
): TimeReportsV2State => {
  switch (action.type) {
    case "SET_LIST_REFRESHING": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      return { ...state, isListRefreshing: action.payload.value };
    }
    case "SET_LIST_LOADING": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      return { ...state, isListLoading: action.payload.value };
    }
    case "SET_LIST_LOADING_MORE": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      return { ...state, isListLoadingMore: action.payload.value };
    }
    case "SET_LIST_REQUEST_ERROR": {
      if (action.requestId !== state.activeListRequestId) return state;
      return { ...state, listError: action.payload };
    }
    case "SET_LIST": {
      if (action.payload.requestId !== state.activeListRequestId) return state;
      if (!action.payload.data) {
        return {
          ...state,
          list: null,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
        };
      }
      const nextItems = action.payload.append
        ? [...(state.list?.items ?? []), ...action.payload.data.items]
        : action.payload.data.items;
      return {
        ...state,
        list: {
          items: nextItems,
          total: action.payload.data.total,
        },
        page: action.payload.page,
        pageSize: action.payload.pageSize,
      };
    }
    case "SET_ACTIVE_LIST_REQUEST_ID": {
      return {
        ...state,
        activeListRequestId: action.payload,
        isListRefreshing: false,
      };
    }
    case "RESET_LIST": {
      return {
        ...state,
        list: null,
        page: 0,
        pageSize: 0,
        listError: null,
        isListLoading: false,
        isListLoadingMore: false,
        isListRefreshing: false,
        activeListRequestId: null,
      };
    }
    case "SET_LIST_QUERY": {
      return { ...state, listQuery: action.payload };
    }
    case "SET_SUMMARY_QUERY": {
      return { ...state, summaryQuery: action.payload };
    }
    case "SET_SUMMARY_LOADING": {
      if (action.payload.requestId !== state.activeSummaryRequestId)
        return state;
      return { ...state, isSummaryLoading: action.payload.value };
    }
    case "SET_ACTIVE_SUMMARY_REQUEST_ID": {
      return { ...state, activeSummaryRequestId: action.payload };
    }
    case "SET_SUMMARY_REQUEST_ERROR": {
      return { ...state, summaryError: action.payload };
    }
    case "SET_SUMMARY": {
      if (action.payload.requestId !== state.activeSummaryRequestId)
        return state;
      return { ...state, summary: action.payload.data };
    }
    case "SET_COUNT_LOADING": {
      if (action.payload.requestId !== state.activeCountRequestId) return state;
      return { ...state, isCountLoading: action.payload.value };
    }
    case "SET_ACTIVE_COUNT_REQUEST_ID": {
      return { ...state, activeCountRequestId: action.payload };
    }
    case "SET_COUNT_REQUEST_ERROR": {
      return { ...state, countError: action.payload };
    }
    case "SET_COUNT": {
      if (action.payload.requestId !== state.activeCountRequestId) return state;
      return { ...state, count: action.payload.value };
    }
    case "SET_PREFERENCES_REQUEST_ERROR": {
      return { ...state, preferencesError: action.payload };
    }
    case "SET_STAFF_REGISTER_LOADING": {
      if (action.payload.requestId !== state.activeStaffRegisterRequestId) {
        return state;
      }
      return { ...state, isStaffRegisterLoading: action.payload.value };
    }
    case "SET_STAFF_REGISTER_LOADING_MORE": {
      if (action.payload.requestId !== state.activeStaffRegisterRequestId) {
        return state;
      }
      return { ...state, isStaffRegisterLoadingMore: action.payload.value };
    }
    case "SET_ACTIVE_STAFF_REGISTER_REQUEST_ID": {
      return { ...state, activeStaffRegisterRequestId: action.payload };
    }
    case "SET_STAFF_REGISTER_REQUEST_ERROR": {
      if (action.payload.requestId !== state.activeStaffRegisterRequestId) {
        return state;
      }
      return { ...state, staffRegisterError: action.payload.value };
    }
    case "SET_STAFF_REGISTER": {
      if (action.payload.requestId !== state.activeStaffRegisterRequestId) {
        return state;
      }
      if (!action.payload.data) {
        return {
          ...state,
          staffRegister: null,
          staffRegisterPage: action.payload.page,
          staffRegisterHasMore: false,
        };
      }
      const appendedItems = action.payload.append
        ? [...(state.staffRegister?.items ?? []), ...action.payload.data.items]
        : action.payload.data.items;
      const nextItems = Array.from(
        new Map(appendedItems.map((item) => [item.id, item])).values(),
      );
      return {
        ...state,
        staffRegister: {
          items: nextItems,
          total: action.payload.data.total,
        },
        staffRegisterPage: action.payload.page,
        staffRegisterHasMore:
          (action.payload.page + 1) * action.payload.pageSize <
          action.payload.data.total,
      };
    }
    case "SET_STAFF_REGISTER_QUERY": {
      return { ...state, staffRegisterQuery: action.payload };
    }
    case "SET_GROUP_BY_OPTION": {
      return {
        ...state,
        preferences: { ...state.preferences, groupByOption: action.payload },
      };
    }
    case "SET_DISPLAY_OPTION": {
      return {
        ...state,
        preferences: { ...state.preferences, displayOption: action.payload },
      };
    }
    case "SET_PREFERENCES": {
      return { ...state, preferences: action.payload };
    }
    default: {
      const unhandledAction: never = action;
      throw new Error(`Unhandled action type: ${unhandledAction}`);
    }
  }
};
